import * as React from 'react';
import { Environment } from '../../../../const/environment';
import ProjectLeftPanelInfoIcon from '../../../components/ProjectLeftPanel/ProjectLeftPanelInfoIcon';
import Formatter from '../../../../services/Formatter';
import Menu from '../../../components/Menu/Menu';
import device from '../../../../services/mini/device';
import { CHARACTER_LIMIT } from '../../../../const/limits';
import { TEXT_TO_SHOW_LENGTH } from '../ScriptArea/ScriptArea';
import scriptDraftService from '../../../../services/ScriptDraftService';
import { IScript, isScriptType } from '../../../../models/IScript';
import { IProject } from '../../../../models/IProject';
import { OpGraphLogIcon } from '../../../components/LogLine/logIcons';
import { ITransform } from '../../../../models/ITransform';
import { removeEmptyRowsFromTransform } from '../TransformMode/getTransformText';
import copyTextToBuffer from '../../../../services/mini/copyTextToBuffer';
import ToasterService from '../../../../services/ToasterService';
import capitalize from '../../../../services/mini/capitalize';


type ScriptItemProps = {
    script: IScript,
    project: IProject,
    isLastTag: boolean,
    environment: Environment
    draggable: boolean,
    deleting: boolean,
    isActive: boolean,
    menuOffset: number,
    githubDeployId: string
    selectedScriptName: string
    searchTerm: string;
    codeString: string;
    lastSearchUpdate: number;
    count?: boolean,
    isLargeFile?: boolean,
    onDuplicate: (script: IScript, isCurrent: boolean) => void;
    onRename: (script: IScript, isCurrent: boolean) => void;
    onDelete: (script: IScript, isCurrent: boolean) => void;
    onExport: (script: IScript, isCurrent: boolean) => void;
    onDiscard?: (script: IScript) => void;
    onOpenScriptDiagram?: (script: IScript) => void;
    onClick: (script: IScript, projectName: string) => void;
}

type ScriptItemState = {
    count: number;
    isLargeFile: boolean,
    hasUnsavedChanges: boolean,
}

class ScriptItem extends React.Component<ScriptItemProps, ScriptItemState> {

    constructor(props) {
        super(props);

        this.state = {
            count: 0,
            isLargeFile: false,
            hasUnsavedChanges: false
        };
    }

    componentDidMount() {
        this.checkForUnsavedChanges(this.props.script);
    }

    componentDidUpdate(prevProps) {
        this.checkForUnsavedChanges(this.props.script);
    }

    checkForUnsavedChanges = async (script) => {
        const hasDraft = await scriptDraftService.hasDraft(script.scriptId);
        let hasChanges = false;
        if (hasDraft) {
            const scriptDraft = await scriptDraftService.getScriptText(script);
            if (script.type === 'transform') {
                try {
                    const scriptDraftTransform: ITransform = JSON.parse(scriptDraft);
                    const scriptTextTransform: ITransform = JSON.parse(script.text);
                    const scriptDraftRemoved = removeEmptyRowsFromTransform(scriptDraftTransform);
                    const scriptTextRemoved = removeEmptyRowsFromTransform(scriptTextTransform);
                    const scriptDraftUnformatted = JSON.stringify(scriptDraftRemoved);
                    const scriptTextUnformatted = JSON.stringify(scriptTextRemoved);
                    hasChanges = scriptDraftUnformatted !== scriptTextUnformatted;
                } catch (error) {
                    console.error("Invalid transform format.");
                }
            } else {
                hasChanges = script.text !== scriptDraft;
            }
        }
        if (hasChanges !== this.state.hasUnsavedChanges) {
            this.setState({ hasUnsavedChanges: hasChanges });
        }
    }

    goTo = (e) => {
        const { script, project, onClick } = this.props;
        onClick(script, project.name);
    }

    deleteScript = (e) => {
        const { script, selectedScriptName: scriptName, onDelete } = this.props;
        onDelete(script, scriptName === script.name);
    }

    exportScript = (e) => {
        const { script, selectedScriptName: scriptName, onExport } = this.props;
        onExport(script, scriptName === script.name);
    }

    discardScriptChanges = (e) => {
        const { script, onDiscard } = this.props;
        if (onDiscard) {
            onDiscard(script);
        }
    }

    openScriptDiagram = (e) => {
        const { script, onOpenScriptDiagram } = this.props;
        if (onOpenScriptDiagram) {
            onOpenScriptDiagram(script);
        }
    }

    renameScript = (e) => {
        const { script, selectedScriptName: scriptName, onRename } = this.props;
        onRename(script, scriptName === script.name);
    }

    duplicateScript = (e) => {
        const { script, selectedScriptName: scriptName, onDuplicate } = this.props;
        onDuplicate(script, scriptName === script.name);
    }

    copyScript = async () => {
        const { script } = this.props;

        let scriptText = await scriptDraftService.getScriptText(script) || script.text;

        if (script.type === 'transform') {
            try {
                scriptText = JSON.stringify(JSON.parse(scriptText), null, 4)
            } catch (error) {

            }
        }

        copyTextToBuffer(scriptText);

        ToasterService.showInfo({ title: `${capitalize(script.type)} content has been copied to the clipboard` });
    }

    getLastChangeText() {
        const timeOptions = {
            allowYesterday: false,
            shortMins: true,
            shortMode: false,
            includeAgo: true
        };
        return Formatter.getHumanTimeFormatted(+this.props.script.updateTs, Date.now(), timeOptions);
    }

    render() {
        const {
            script,
            isLastTag,
            environment,
            deleting,
            menuOffset,
            githubDeployId,
            count,
            isLargeFile,
        } = this.props;

        const {
            hasUnsavedChanges
        } = this.state;

        const isCiCd = githubDeployId !== '';
        const edited = hasUnsavedChanges && isLastTag && environment === Environment.Staging && !isCiCd;
        const locked = isCiCd || (!isLastTag || environment !== Environment.Staging);

        return <div onClick={this.goTo}
            title={script.name}
            className={`script-item ${edited && 'edited'} ${deleting && 'disabled'} ${!isLastTag && 'not-last'}`}>
            <span className="script-item_content">
                <ProjectLeftPanelInfoIcon>
                    <i className={`icon icon-alan-lock-small lock-icon ${!locked && 'disappear'}`} />
                </ProjectLeftPanelInfoIcon>
                {script.name}
                {script.error ?
                    <div className="script-item_info error">Script is unavailable</div> :
                    <div className="script-item_info">
                        {edited ?
                            `Unsaved changes` :
                            `${this.getLastChangeText()}`
                        }
                    </div>
                }
            </span>
            {!!count &&
                <span className="script-item__counter-wrap">
                    <i className="icon icon-alan-search script-item__icon"></i>
                    <span className="script-item__count">{count}{!!(isLargeFile && count) && '+'}</span>
                </span>
            }
            {(isLastTag && environment === Environment.Staging) ?
                <Menu
                    fixed={device.isDesktop() ? true : false}
                    width={162}
                    itemHeight={32}
                    top={device.isDesktop() ? undefined : (menuOffset ? menuOffset : undefined)}
                >
                    {this.props.onOpenScriptDiagram &&
                        <div onClick={this.openScriptDiagram}>
                            <OpGraphLogIcon className="script-item__graph-icon" />Get script diagram
                        </div>}
                    <div onClick={this.renameScript}>
                        <i className="icon icon-alan-edit" />Rename
                    </div>
                    <div onClick={this.duplicateScript} className="script-item__duplicate">
                        <i className="icon icon-alan-duplicate" />Duplicate {isScriptType(script) ? 'script' : 'transform'}
                    </div>

                    <div onClick={this.copyScript}>
                        <i className="icon icon-alan-test-copy" />Copy to Clipboard
                    </div>

                    <div onClick={this.exportScript}>
                        <i className="icon icon-alan-load-down" />Export
                    </div>

                    {this.props.onDiscard &&
                        <div onClick={this.discardScriptChanges} className={!hasUnsavedChanges ? 'disabled' : ''}>
                            <i className="icon icon-alan-arrow-round-right-left-fat" />Discard local changes
                        </div>}
                    <div onClick={this.deleteScript}>
                        <i className="icon icon-alan-trash" />Delete
                    </div>
                </Menu> :
                <Menu
                    fixed={device.isDesktop() ? true : false}
                    width={162}
                    top={device.isDesktop() ? undefined : (menuOffset ? menuOffset : undefined)}
                >
                    <div onClick={this.copyScript}>
                        <i className="icon icon-alan-test-copy" />Copy to Clipboard
                    </div>

                    <div onClick={this.exportScript}>
                        <i className="icon icon-alan-load-down" />Export
                    </div>
                </Menu>
            }

        </div>;
    }
}

export default ScriptItem;